import React from 'react';
import { useParams } from 'react-router-dom';

import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Legend, XAxis, YAxis, Tooltip, Bar, CartesianGrid, ResponsiveContainer } from 'recharts';

import Spinner from '../../../components/Spinner';
import ErrorBox from '../../../components/ErrorBox';
import NoData from '../../../components/NoData';

const keysToNamesMap = {
  pos: 'Positive',
  neg: 'Negative',
  gap: ''
};

const formatTooltip = (value, name) => {
  if (name === 'gap') {
    return [];
  }
  return [value, keysToNamesMap[name]];
};

const renderLegendText = (value) => {
  const nameMap = {
    pos: 'Positive',
    neg: 'Negative',
  };
  
  return nameMap[value] || value;
};

const rowHeight = 40;

const fetchSecondaryCategories = async ({ queryKey }) => {
  const [, locationId] = queryKey;
  
  const { data } = await axios.get(`${import.meta.env.VITE_API_URL}api/v4/locachecker/secondaryInsights`, {
    params: { locationId },
  });
  
  return data.data;
};

const renderContent = (secondaryCategories, isLoading, isError, chartHeight) => {
  if (isLoading) {
    return <Spinner height={450}/>;
  }
  
  if (isError) {
    return <ErrorBox description="We encountered an issue loading your Secondary Categories data. Please try again."/>;
  }
  
  if (secondaryCategories?.length === 0) {
    return <NoData description={'No data to display for Secondary Categories'}/>
    
  }
  
  return <ResponsiveContainer width="100%" height={chartHeight}>
    <BarChart data={secondaryCategories} layout="vertical" barCategoryGap="20%">
      <Legend
        formatter={renderLegendText}
        verticalAlign="top"
        height={50}
        align="right"
      />
      
      <CartesianGrid strokeDasharray="3 3"/>
      <XAxis type="number"/>
      <Tooltip formatter={formatTooltip}/>
      <YAxis width={80} type="category" dataKey="subject"/>
      
      <Bar dataKey={'pos'} stackId="a" fill={'#7ec95a'}/>
      <Bar dataKey={'neg'} stackId="a" fill={'#e55039'}/>
      <Bar dataKey={'gap'} stackId="a" fill="transparent"/>
    </BarChart>
  </ResponsiveContainer>
}

const SecondaryInsights = () => {
  const { locationId } = useParams();
  
  const {
    data: secondaryCategories,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['secondaryCategories', locationId],
    queryFn: fetchSecondaryCategories,
    enabled: !!locationId,
  });
  
  const chartHeight = secondaryCategories?.length * rowHeight;
  
  return (
    <div
      className="flex-1 w-1/2 h-auto min-h-[450px] [&>svg]:!overflow-visible rounded-md bg-white p-8 px-10 shadow-[0px_4px_8px_0px_#0000000D]">
      <div className="border-b-2 border-gray-500">
        <h3>Secondary Categories</h3>
      </div>
      
      <div className="relative flex w-full py-5 px-0 h-[500px] min-h-[500px] text-xs">
        {renderContent(secondaryCategories, isLoading, isError, chartHeight)}
      </div>
    </div>
  );
};

export default SecondaryInsights;

